import { useState } from 'react';
import AdminTabs from '../AdminTabs';
import { useAdminSubTab } from '../adminRoute';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { FormField } from '@/components/ui/FormField';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { LoadingState, EmptyState, ErrorState } from '@/components/ui/States';
import { useAuth } from '@/hooks/useAuth';
import {
  useLabDepartments, useLabTestCategories, useLabSampleTypes, useLabContainers,
  useLabTests, useLabTest, useLabPackages, useReferenceLabs,
  useSaveDepartment, useSaveTestCategory, useSaveSampleType, useSaveContainer, useSaveReferenceLab,
  useSaveTest, useSaveParameter, useSaveReferenceRange, useSavePackage,
} from '@/features/laboratory/hooks';
import type { LabDataType, LabParameter, LabReferenceRange, LabTest, ProcessingType } from '@/features/laboratory/types';

/**
 * Laboratory configuration — an ADMIN PANEL tab (Admin Panel → Laboratory),
 * not a tab inside the Laboratory module.
 *
 * It lives here because editing a test, a parameter or — above all — a
 * REFERENCE RANGE changes what "normal" means for every future patient at this
 * hospital. That is a governance act, and it belongs beside the other
 * master-data screens (Formulary, Specialties, Wards, Billing Settings) rather
 * than on a bench screen someone uses mid-shift. The grant behind it,
 * `admin:laboratory`, was always an `admin:*` grant for exactly that reason;
 * this placement puts the screen where its permission already said it belonged.
 *
 * The laboratory ITSELF still reads all of this constantly — the test picker,
 * the entry sheet's units and ranges, the collection room's container list.
 * Those reads are gated on the plain `laboratory` module and are unaffected.
 */

const DATA_TYPES: Array<{ value: LabDataType; label: string }> = [
  { value: 'numeric',              label: 'Numeric' },
  { value: 'text',                 label: 'Free text' },
  { value: 'positive_negative',    label: 'Positive / Negative' },
  { value: 'reactive_nonreactive', label: 'Reactive / Non-Reactive' },
  { value: 'normal_abnormal',      label: 'Normal / Abnormal' },
];

export default function MastersTab() {
  const { user } = useAuth();
  const tabs = useAdminSubTab('laboratory');
  const tab = tabs.active;
  const canEdit = !!user?.modules?.includes('admin:laboratory');

  return (
    <div className="space-y-3">
      {/*
        There is no "you can only view this" banner, because AdminModule gates
        this whole tab on `admin:laboratory` — reaching this screen already
        implies the grant. `canEdit` is still threaded through the panels below
        as defence in depth: it keeps the component correct if it is ever
        embedded somewhere less restricted, and it mirrors the check every
        write endpoint makes server-side regardless.
      */}
      <AdminTabs state={tabs} className="mb-1" />
      {tab === 'tests' && <TestsPanel canEdit={canEdit} />}
      {tab === 'departments' && <SimpleMaster canEdit={canEdit} kind="departments" />}
      {tab === 'categories' && <SimpleMaster canEdit={canEdit} kind="categories" />}
      {tab === 'sample-types' && <SimpleMaster canEdit={canEdit} kind="sample-types" />}
      {tab === 'containers' && <SimpleMaster canEdit={canEdit} kind="containers" />}
      {tab === 'packages' && <PackagesPanel canEdit={canEdit} />}
      {tab === 'reference-labs' && <SimpleMaster canEdit={canEdit} kind="reference-labs" />}
    </div>
  );
}

// ── The four simple code/name masters ────────────────────────────

type SimpleKind = 'departments' | 'categories' | 'sample-types' | 'containers' | 'reference-labs';

/** Which extra fields each simple master carries, and what they are called. */
const SIMPLE_FIELDS: Record<SimpleKind, Array<{ key: string; label: string; type?: 'number' | 'textarea' }>> = {
  departments:    [{ key: 'description', label: 'Description', type: 'textarea' }, { key: 'sequence', label: 'Display order', type: 'number' }],
  categories:     [{ key: 'description', label: 'Description', type: 'textarea' }],
  'sample-types': [
    { key: 'min_volume_ml', label: 'Minimum volume (mL)', type: 'number' },
    { key: 'storage_condition', label: 'Storage condition' },
    { key: 'stability', label: 'Stability' },
    { key: 'collection_instructions', label: 'Collection instructions', type: 'textarea' },
    { key: 'processing_instructions', label: 'Processing instructions', type: 'textarea' },
  ],
  containers:     [
    { key: 'cap_colour', label: 'Cap colour' },
    { key: 'additive', label: 'Additive' },
    { key: 'default_volume_ml', label: 'Default volume (mL)', type: 'number' },
  ],
  'reference-labs': [
    { key: 'contact_person', label: 'Contact person' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'address', label: 'Address', type: 'textarea' },
    { key: 'notes', label: 'Notes', type: 'textarea' },
  ],
};

const SIMPLE_TITLE: Record<SimpleKind, string> = {
  departments: 'department', categories: 'test category',
  'sample-types': 'sample type', containers: 'container',
  'reference-labs': 'reference laboratory',
};

function SimpleMaster({ kind, canEdit }: { kind: SimpleKind; canEdit: boolean }) {
  const departments = useLabDepartments();
  const categories = useLabTestCategories();
  const sampleTypes = useLabSampleTypes();
  const containers = useLabContainers();
  // include_inactive: a deactivated reference lab must stay visible here so it
  // can be re-activated — the pickers elsewhere list active ones only.
  const referenceLabs = useReferenceLabs(true);
  const saveDept = useSaveDepartment();
  const saveCat = useSaveTestCategory();
  const saveSample = useSaveSampleType();
  const saveContainer = useSaveContainer();
  const saveReferenceLab = useSaveReferenceLab();

  const query = kind === 'departments' ? departments
    : kind === 'categories' ? categories
    : kind === 'sample-types' ? sampleTypes
    : kind === 'reference-labs' ? referenceLabs
    : containers;
  const save = kind === 'departments' ? saveDept
    : kind === 'categories' ? saveCat
    : kind === 'sample-types' ? saveSample
    : kind === 'reference-labs' ? saveReferenceLab
    : saveContainer;

  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fields = SIMPLE_FIELDS[kind];

  const rows = (query.data ?? []) as unknown as Array<Record<string, unknown>>;

  const submit = async () => {
    if (!editing) return;
    setError(null);
    const { id, ...body } = editing;
    try {
      await save.mutateAsync({ id: id as number | undefined, body });
      setEditing(null);
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <div className="space-y-2">
      {canEdit && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setEditing({ code: '', name: '', active: true })}>
            + New {SIMPLE_TITLE[kind]}
          </Button>
        </div>
      )}
      {query.isLoading && <LoadingState label="Loading…" />}
      {!query.isLoading && rows.length === 0 && <EmptyState label={`No ${SIMPLE_TITLE[kind]}s configured.`} />}
      {rows.length > 0 && (
        <div className="rounded-xl border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                {fields.map((f) => <TableHead key={f.key}>{f.label}</TableHead>)}
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={String(r.id)}>
                  <TableCell className="font-mono text-[12px]">{String(r.code)}</TableCell>
                  <TableCell>
                    {String(r.name)}
                    {r.active === false && <Badge variant="neutral" className="ml-1">Inactive</Badge>}
                  </TableCell>
                  {fields.map((f) => (
                    <TableCell key={f.key} className="max-w-[220px] truncate text-[12px] text-muted-foreground">
                      {r[f.key] === null || r[f.key] === undefined ? '—' : String(r[f.key])}
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    {canEdit && <Button variant="link" size="sm" onClick={() => setEditing({ ...r })}>Edit</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => { if (!o) { setEditing(null); setError(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit' : 'New'} {SIMPLE_TITLE[kind]}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
              <FormField label="Code" required>
                <Input value={String(editing.code ?? '')} onChange={(e) => setEditing({ ...editing, code: e.target.value })} />
              </FormField>
              <FormField label="Name" required>
                <Input value={String(editing.name ?? '')} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </FormField>
              {fields.map((f) => (
                <FormField key={f.key} label={f.label}>
                  {f.type === 'textarea' ? (
                    <Textarea rows={2} value={String(editing[f.key] ?? '')}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} />
                  ) : (
                    <Input type={f.type === 'number' ? 'number' : 'text'} value={String(editing[f.key] ?? '')}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} />
                  )}
                </FormField>
              ))}
              {editing.id !== undefined && (
                <label className="flex items-center gap-2 text-[13px]">
                  <input type="checkbox" checked={editing.active !== false}
                    onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                  Active
                </label>
              )}
              {error && <ErrorState message={error} />}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEditing(null); setError(null); }}>Cancel</Button>
            <Button disabled={save.isPending} onClick={submit}>{save.isPending ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Tests, parameters and reference ranges ───────────────────────

function TestsPanel({ canEdit }: { canEdit: boolean }) {
  const [q, setQ] = useState('');
  const [openTestId, setOpenTestId] = useState<number | null>(null);
  const [editingTest, setEditingTest] = useState<Partial<LabTest> | null>(null);
  const tests = useLabTests({ q: q.trim() || undefined, include_inactive: true });

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input className="flex-1" placeholder="Search tests…" value={q} onChange={(e) => setQ(e.target.value)} />
        {canEdit && (
          <Button size="sm" onClick={() => setEditingTest({ code: '', name: '', price: '0', gst_percent: '0', active: true })}>
            + New test
          </Button>
        )}
      </div>

      {tests.isLoading && <LoadingState label="Loading tests…" />}
      {!tests.isLoading && (tests.data ?? []).length === 0 && <EmptyState label="No tests configured." />}

      {(tests.data ?? []).length > 0 && (
        <div className="rounded-xl border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Test</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Sample</TableHead>
                <TableHead className="text-right">Parameters</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(tests.data ?? []).map((t) => (
                <TableRow key={t.id} className="cursor-pointer" onClick={() => setOpenTestId(t.id)}>
                  <TableCell className="font-mono text-[12px]">{t.code}</TableCell>
                  <TableCell>
                    {t.name}
                    {!t.active && <Badge variant="neutral" className="ml-1">Inactive</Badge>}
                    {t.fasting_required && <Badge variant="warn" className="ml-1">Fasting</Badge>}
                    {t.processing_type === 'outsourced' && (
                      <Badge variant={t.reference_lab_active === false ? 'danger' : 'info'} className="ml-1"
                        title={t.reference_lab_active === false ? 'Its reference laboratory is inactive — this test cannot be ordered until re-pointed' : undefined}>
                        Outside · {t.reference_lab_name ?? '—'}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-[12px]">{t.department_name ?? '—'}</TableCell>
                  <TableCell className="text-[12px]">
                    {[t.sample_type_name, t.container_name].filter(Boolean).join(' · ') || '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {Number(t.parameter_count ?? 0) === 0
                      ? <Badge variant="warn">none</Badge>
                      : t.parameter_count}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">Rs. {Number(t.price).toFixed(2)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="link" size="sm" onClick={(e) => { e.stopPropagation(); setOpenTestId(t.id); }}>
                      Configure
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <TestEditor test={editingTest} onClose={() => setEditingTest(null)} />
      <TestDetailDialog testId={openTestId} canEdit={canEdit} onClose={() => setOpenTestId(null)}
        onEdit={(t) => { setOpenTestId(null); setEditingTest(t); }} />
    </div>
  );
}

function TestEditor({ test, onClose }: { test: Partial<LabTest> | null; onClose: () => void }) {
  const departments = useLabDepartments();
  const categories = useLabTestCategories();
  const sampleTypes = useLabSampleTypes();
  const containers = useLabContainers();
  const referenceLabs = useReferenceLabs();
  const save = useSaveTest();
  const [draft, setDraft] = useState<Partial<LabTest>>({});
  const [error, setError] = useState<string | null>(null);

  // Re-seed whenever a different test is opened.
  const key = test?.id ?? (test ? 'new' : null);
  const [seededKey, setSeededKey] = useState<number | string | null>(null);
  if (key !== seededKey) { setSeededKey(key); setDraft(test ?? {}); setError(null); }

  const set = (patch: Partial<LabTest>) => setDraft((d) => ({ ...d, ...patch }));

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({
        id: draft.id,
        body: {
          code: draft.code, name: draft.name, short_name: draft.short_name || null,
          department_id: draft.department_id || null, category_id: draft.category_id || null,
          sample_type_id: draft.sample_type_id || null, container_id: draft.container_id || null,
          method: draft.method || null, sample_volume_ml: draft.sample_volume_ml || null,
          tat_hours: draft.tat_hours || null, fasting_required: !!draft.fasting_required,
          patient_preparation: draft.patient_preparation || null,
          price: draft.price ?? 0, gst_percent: draft.gst_percent ?? 0,
          processing_type: draft.processing_type ?? 'internal',
          default_reference_lab_id: draft.processing_type === 'outsourced' ? (draft.default_reference_lab_id || null) : null,
          active: draft.active !== false,
        },
      });
      onClose();
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <Dialog open={test !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{draft.id ? 'Edit test' : 'New test'}</DialogTitle></DialogHeader>
        <div className="max-h-[65vh] space-y-3 overflow-y-auto pr-1">
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Code" required>
              <Input value={draft.code ?? ''} onChange={(e) => set({ code: e.target.value })} />
            </FormField>
            <FormField label="Short name">
              <Input value={draft.short_name ?? ''} onChange={(e) => set({ short_name: e.target.value })} />
            </FormField>
          </div>
          <FormField label="Test name" required>
            <Input value={draft.name ?? ''} onChange={(e) => set({ name: e.target.value })} />
          </FormField>
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Department">
              <select className="h-9 w-full rounded-lg border border-border bg-card px-2 text-[13px]"
                value={draft.department_id ?? ''} onChange={(e) => set({ department_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">—</option>
                {(departments.data ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </FormField>
            <FormField label="Category">
              <select className="h-9 w-full rounded-lg border border-border bg-card px-2 text-[13px]"
                value={draft.category_id ?? ''} onChange={(e) => set({ category_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">—</option>
                {(categories.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </FormField>
            <FormField label="Sample type">
              <select className="h-9 w-full rounded-lg border border-border bg-card px-2 text-[13px]"
                value={draft.sample_type_id ?? ''} onChange={(e) => set({ sample_type_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">—</option>
                {(sampleTypes.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </FormField>
            <FormField label="Container">
              <select className="h-9 w-full rounded-lg border border-border bg-card px-2 text-[13px]"
                value={draft.container_id ?? ''} onChange={(e) => set({ container_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">—</option>
                {(containers.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </FormField>
            <FormField label="Method">
              <Input value={draft.method ?? ''} onChange={(e) => set({ method: e.target.value })} />
            </FormField>
            <FormField label="Turnaround (hours)">
              <Input type="number" value={draft.tat_hours ?? ''} onChange={(e) => set({ tat_hours: e.target.value })} />
            </FormField>
            <FormField label="Price (Rs.)">
              <Input type="number" value={draft.price ?? ''} onChange={(e) => set({ price: e.target.value })} />
            </FormField>
            <FormField label="GST %">
              <Input type="number" value={draft.gst_percent ?? ''} onChange={(e) => set({ gst_percent: e.target.value })} />
            </FormField>
          </div>
          {/*
            Processing type is per TEST, never per category or department — a
            hospital that sends Vitamin D out may run the rest of Biochemistry
            itself. Existing orders keep the routing they were placed with.
          */}
          <div className="grid gap-3 rounded-lg border border-border p-3 sm:grid-cols-2">
            <FormField label="Processing type" required>
              <select className="h-9 w-full rounded-lg border border-border bg-card px-2 text-[13px]"
                value={draft.processing_type ?? 'internal'}
                onChange={(e) => set({ processing_type: e.target.value as ProcessingType })}>
                <option value="internal">Internal lab</option>
                <option value="outsourced">Outside / Reference lab</option>
              </select>
            </FormField>
            {draft.processing_type === 'outsourced' && (
              <FormField label="Default reference laboratory" required>
                <select className="h-9 w-full rounded-lg border border-border bg-card px-2 text-[13px]"
                  value={draft.default_reference_lab_id ?? ''}
                  onChange={(e) => set({ default_reference_lab_id: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">Choose…</option>
                  {(referenceLabs.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </FormField>
            )}
            {draft.processing_type === 'outsourced' && (referenceLabs.data ?? []).length === 0 && (
              <p className="text-[12px] text-caution-ink sm:col-span-2">
                No reference laboratories are configured yet — add one under 🚚 Reference labs first.
              </p>
            )}
          </div>
          <label className="flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={!!draft.fasting_required}
              onChange={(e) => set({ fasting_required: e.target.checked })} />
            Fasting required
          </label>
          <FormField label="Patient preparation">
            <Textarea rows={2} value={draft.patient_preparation ?? ''}
              onChange={(e) => set({ patient_preparation: e.target.value })} />
          </FormField>
          <label className="flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={draft.active !== false} onChange={(e) => set({ active: e.target.checked })} />
            Active — a deactivated test can no longer be ordered
          </label>
          {error && <ErrorState message={error} />}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={save.isPending} onClick={submit}>{save.isPending ? 'Saving…' : 'Save test'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TestDetailDialog({ testId, canEdit, onClose, onEdit }: {
  testId: number | null; canEdit: boolean; onClose: () => void; onEdit: (t: LabTest) => void;
}) {
  const { data, isLoading, error, refetch } = useLabTest(testId);
  const [editingParam, setEditingParam] = useState<Partial<LabParameter> | null>(null);
  const [rangeFor, setRangeFor] = useState<{ parameter: LabParameter; range: Partial<LabReferenceRange> } | null>(null);

  return (
    <>
      <Dialog open={testId !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-3xl">
          {isLoading && <LoadingState label="Loading…" />}
          {error && <ErrorState message={(error as Error).message} onRetry={() => refetch()} />}
          {data && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {data.name} <span className="font-mono text-[12px] text-muted-foreground">{data.code}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
                <div className="text-[12px] text-muted-foreground">
                  {[data.department_name, data.category_name, data.sample_type_name, data.container_name,
                    data.method, data.tat_hours ? `TAT ${Number(data.tat_hours)}h` : null,
                    `Rs. ${Number(data.price).toFixed(2)}`].filter(Boolean).join(' · ')}
                </div>
                {canEdit && <Button variant="ghost" size="sm" onClick={() => onEdit(data)}>Edit test details</Button>}

                {data.parameters.length === 0 && (
                  <div className="rounded-lg border border-warn-soft bg-warn-soft px-3 py-2 text-[12px] text-warn-ink">
                    This test has no parameters, so no result can be entered against it. Add at least one.
                  </div>
                )}

                <div className="space-y-3">
                  {data.parameters.map((p) => (
                    <div key={p.id} className="rounded-xl border border-border p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <span className="text-[13px] font-semibold">{p.name}</span>
                          <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">{p.code}</span>
                          <Badge variant="info" className="ml-2">
                            {DATA_TYPES.find((d) => d.value === p.data_type)?.label ?? p.data_type}
                          </Badge>
                          {p.unit && <span className="ml-2 text-[11px] text-muted-foreground">{p.unit}</span>}
                          {!p.active && <Badge variant="neutral" className="ml-1">Inactive</Badge>}
                        </div>
                        {canEdit && (
                          <div className="flex gap-1">
                            <Button variant="link" size="sm" onClick={() => setEditingParam(p)}>Edit</Button>
                            <Button variant="link" size="sm"
                              onClick={() => setRangeFor({ parameter: p, range: { gender: 'any' } })}>
                              + Range
                            </Button>
                          </div>
                        )}
                      </div>

                      <div className="mt-2 space-y-1">
                        {(p.reference_ranges ?? []).length === 0 && (
                          <div className="text-[11px] text-warn-ink">
                            No reference range — results for this parameter will be reported as “not evaluated”,
                            never as normal.
                          </div>
                        )}
                        {(p.reference_ranges ?? []).map((r) => (
                          <div key={r.id} className="flex items-center justify-between rounded-lg bg-muted/40 px-2 py-1 text-[11px]">
                            <span>
                              <span className="font-semibold">
                                {r.display_text
                                  ?? (r.normal_text
                                    ?? `${r.normal_low ? Number(r.normal_low) : '—'} – ${r.normal_high ? Number(r.normal_high) : '—'}`)}
                              </span>
                              {r.unit ? ` ${r.unit}` : ''}
                              <span className="text-muted-foreground">
                                {' · '}{r.gender === 'any' ? 'any sex' : r.gender}
                                {r.age_min_days !== null || r.age_max_days !== null
                                  ? ` · ${describeAge(r.age_min_days, r.age_max_days)}`
                                  : ' · any age'}
                                {r.pregnancy !== null ? (r.pregnancy ? ' · pregnant' : ' · not pregnant') : ''}
                                {r.method ? ` · ${r.method}` : ''}
                                {r.critical_low !== null || r.critical_high !== null
                                  ? ` · critical ${r.critical_low ? `< ${Number(r.critical_low)}` : ''}${r.critical_low && r.critical_high ? ' / ' : ''}${r.critical_high ? `> ${Number(r.critical_high)}` : ''}`
                                  : ''}
                              </span>
                              {!r.active && <Badge variant="neutral" className="ml-1">Inactive</Badge>}
                            </span>
                            {canEdit && (
                              <Button variant="link" size="sm" onClick={() => setRangeFor({ parameter: p, range: r })}>
                                Edit
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {canEdit && (
                  <Button variant="ghost" size="sm"
                    onClick={() => setEditingParam({ test_id: data.id, code: '', name: '', data_type: 'numeric', decimal_precision: 2 })}>
                    + Add parameter
                  </Button>
                )}
              </div>
              <DialogFooter><Button variant="ghost" onClick={onClose}>Close</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ParameterEditor
        parameter={editingParam}
        testId={testId}
        onClose={() => setEditingParam(null)}
      />
      <RangeEditor
        target={rangeFor}
        onClose={() => setRangeFor(null)}
      />
    </>
  );
}

function describeAge(minDays: number | null, maxDays: number | null): string {
  const toText = (d: number) => d < 60 ? `${d}d` : d < 730 ? `${Math.round(d / 30)}mo` : `${Math.round(d / 365.2425)}y`;
  if (minDays !== null && maxDays !== null) return `${toText(minDays)}–${toText(maxDays)}`;
  if (minDays !== null) return `${toText(minDays)}+`;
  if (maxDays !== null) return `under ${toText(maxDays)}`;
  return 'any age';
}

function ParameterEditor({ parameter, testId, onClose }: {
  parameter: Partial<LabParameter> | null; testId: number | null; onClose: () => void;
}) {
  const save = useSaveParameter();
  const [draft, setDraft] = useState<Partial<LabParameter>>({});
  const [error, setError] = useState<string | null>(null);
  const key = parameter?.id ?? (parameter ? 'new' : null);
  const [seededKey, setSeededKey] = useState<number | string | null>(null);
  if (key !== seededKey) { setSeededKey(key); setDraft(parameter ?? {}); setError(null); }

  const set = (patch: Partial<LabParameter>) => setDraft((d) => ({ ...d, ...patch }));
  const qualitative = draft.data_type && draft.data_type !== 'numeric';

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({
        id: draft.id,
        testId: draft.test_id ?? testId ?? undefined,
        body: {
          code: draft.code, name: draft.name, data_type: draft.data_type ?? 'numeric',
          unit: draft.unit || null, result_format: draft.result_format || null,
          decimal_precision: draft.decimal_precision ?? null,
          default_reference: draft.default_reference || null,
          normal_value: draft.normal_value || null,
          critical_low: draft.critical_low || null, critical_high: draft.critical_high || null,
          sequence: draft.sequence ?? null, active: draft.active !== false,
        },
      });
      onClose();
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <Dialog open={parameter !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{draft.id ? 'Edit parameter' : 'New parameter'}</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Code" required>
              <Input value={draft.code ?? ''} onChange={(e) => set({ code: e.target.value })} />
            </FormField>
            <FormField label="Display order">
              <Input type="number" value={draft.sequence ?? ''} onChange={(e) => set({ sequence: Number(e.target.value) })} />
            </FormField>
          </div>
          <FormField label="Name" required>
            <Input value={draft.name ?? ''} onChange={(e) => set({ name: e.target.value })} />
          </FormField>
          <FormField label="Result type" required>
            <select className="h-9 w-full rounded-lg border border-border bg-card px-2 text-[13px]"
              value={draft.data_type ?? 'numeric'}
              onChange={(e) => set({ data_type: e.target.value as LabDataType })}>
              {DATA_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </FormField>
          {!qualitative && (
            <div className="grid gap-3 sm:grid-cols-3">
              <FormField label="Unit">
                <Input value={draft.unit ?? ''} onChange={(e) => set({ unit: e.target.value })} />
              </FormField>
              <FormField label="Decimals">
                <Input type="number" value={draft.decimal_precision ?? ''}
                  onChange={(e) => set({ decimal_precision: Number(e.target.value) })} />
              </FormField>
              <FormField label="Format hint">
                <Input value={draft.result_format ?? ''} onChange={(e) => set({ result_format: e.target.value })} />
              </FormField>
            </div>
          )}
          {qualitative && (
            <FormField label="Which value counts as normal" labelExtra={<span className="text-[11px] text-muted-foreground">e.g. Non-Reactive</span>}>
              <Input value={draft.normal_value ?? ''} onChange={(e) => set({ normal_value: e.target.value })} />
            </FormField>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Critical low (fallback)">
              <Input type="number" value={draft.critical_low ?? ''} onChange={(e) => set({ critical_low: e.target.value })} />
            </FormField>
            <FormField label="Critical high (fallback)">
              <Input type="number" value={draft.critical_high ?? ''} onChange={(e) => set({ critical_high: e.target.value })} />
            </FormField>
          </div>
          <p className="text-[11px] text-muted-foreground">
            A matching reference range’s own critical bounds win over these — set these only for a test whose
            critical thresholds genuinely don’t vary by age or sex.
          </p>
          {draft.id !== undefined && (
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={draft.active !== false} onChange={(e) => set({ active: e.target.checked })} />
              Active
            </label>
          )}
          {error && <ErrorState message={error} />}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={save.isPending} onClick={submit}>{save.isPending ? 'Saving…' : 'Save parameter'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The reference-range editor.
 *
 * Age is entered in DAYS because that is what the engine matches on, and it is
 * the only unit in which "0–7 days" is expressible — which is the entire
 * reason paediatric ranges need their own rows. The helper buttons cover the
 * common adult/paediatric boundaries so nobody has to multiply by 365 at 2am.
 */
function RangeEditor({ target, onClose }: {
  target: { parameter: LabParameter; range: Partial<LabReferenceRange> } | null;
  onClose: () => void;
}) {
  const save = useSaveReferenceRange();
  const [draft, setDraft] = useState<Partial<LabReferenceRange>>({});
  const [error, setError] = useState<string | null>(null);
  const key = target?.range?.id ?? (target ? `new-${target.parameter.id}` : null);
  const [seededKey, setSeededKey] = useState<number | string | null>(null);
  if (key !== seededKey) { setSeededKey(key); setDraft(target?.range ?? {}); setError(null); }

  const set = (patch: Partial<LabReferenceRange>) => setDraft((d) => ({ ...d, ...patch }));
  const qualitative = target?.parameter.data_type !== 'numeric';

  const submit = async () => {
    if (!target) return;
    setError(null);
    try {
      await save.mutateAsync({
        id: draft.id,
        parameterId: target.parameter.id,
        body: {
          gender: draft.gender ?? 'any',
          age_min_days: draft.age_min_days ?? null,
          age_max_days: draft.age_max_days ?? null,
          pregnancy: draft.pregnancy ?? null,
          method: draft.method || null,
          unit: draft.unit || target.parameter.unit || null,
          normal_low: draft.normal_low || null, normal_high: draft.normal_high || null,
          normal_text: draft.normal_text || null,
          critical_low: draft.critical_low || null, critical_high: draft.critical_high || null,
          display_text: draft.display_text || null,
          notes: draft.notes || null,
          active: draft.active !== false,
        },
      });
      onClose();
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <Dialog open={target !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Reference range — {target?.parameter.name}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
          <p className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground">
            This decides what “normal” means for every future result on this parameter. The most specific
            matching range wins — a sex- and age-bounded row beats a general one.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Applies to">
              <select className="h-9 w-full rounded-lg border border-border bg-card px-2 text-[13px]"
                value={draft.gender ?? 'any'}
                onChange={(e) => set({ gender: e.target.value as LabReferenceRange['gender'] })}>
                <option value="any">Any sex</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </FormField>
            <FormField label="Pregnancy">
              <select className="h-9 w-full rounded-lg border border-border bg-card px-2 text-[13px]"
                value={draft.pregnancy === null || draft.pregnancy === undefined ? '' : String(draft.pregnancy)}
                onChange={(e) => set({ pregnancy: e.target.value === '' ? null : e.target.value === 'true' })}>
                <option value="">Regardless</option>
                <option value="true">Pregnant only</option>
                <option value="false">Not pregnant only</option>
              </select>
            </FormField>
            <FormField label="Age from (days)">
              <Input type="number" value={draft.age_min_days ?? ''}
                onChange={(e) => set({ age_min_days: e.target.value === '' ? null : Number(e.target.value) })} />
            </FormField>
            <FormField label="Age to (days, exclusive)">
              <Input type="number" value={draft.age_max_days ?? ''}
                onChange={(e) => set({ age_max_days: e.target.value === '' ? null : Number(e.target.value) })} />
            </FormField>
          </div>
          <div className="flex flex-wrap gap-1">
            {[
              { label: 'Newborn 0–7d', min: 0, max: 7 },
              { label: 'Infant 7d–1y', min: 7, max: 365 },
              { label: 'Child 1–13y', min: 365, max: 4745 },
              { label: 'Adult 13y+', min: 4745, max: null },
              { label: 'Any age', min: null, max: null },
            ].map((preset) => (
              <button key={preset.label} type="button"
                className="rounded-full border border-border px-2 py-0.5 text-[11px] hover:bg-muted"
                onClick={() => set({ age_min_days: preset.min, age_max_days: preset.max })}>
                {preset.label}
              </button>
            ))}
          </div>

          {qualitative ? (
            <FormField label="Normal value" required>
              <Input value={draft.normal_text ?? ''} onChange={(e) => set({ normal_text: e.target.value })}
                placeholder="e.g. Non-Reactive" />
            </FormField>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <FormField label="Normal low">
                  <Input type="number" value={draft.normal_low ?? ''} onChange={(e) => set({ normal_low: e.target.value })} />
                </FormField>
                <FormField label="Normal high">
                  <Input type="number" value={draft.normal_high ?? ''} onChange={(e) => set({ normal_high: e.target.value })} />
                </FormField>
                <FormField label="Unit">
                  <Input value={draft.unit ?? target?.parameter.unit ?? ''} onChange={(e) => set({ unit: e.target.value })} />
                </FormField>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField label="Critical low">
                  <Input type="number" value={draft.critical_low ?? ''} onChange={(e) => set({ critical_low: e.target.value })} />
                </FormField>
                <FormField label="Critical high">
                  <Input type="number" value={draft.critical_high ?? ''} onChange={(e) => set({ critical_high: e.target.value })} />
                </FormField>
              </div>
              <p className="text-[11px] text-muted-foreground">
                A result crossing a critical bound raises a critical-result record and notifies the ordering
                doctor. Leave blank for a parameter that has no critical threshold.
              </p>
            </>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Method (optional)" labelExtra={<span className="text-[11px] text-muted-foreground">only applies to this method</span>}>
              <Input value={draft.method ?? ''} onChange={(e) => set({ method: e.target.value })} />
            </FormField>
            <FormField label="Printed as (optional)">
              <Input value={draft.display_text ?? ''} onChange={(e) => set({ display_text: e.target.value })}
                placeholder="e.g. 13 - 17" />
            </FormField>
          </div>

          {draft.id !== undefined && (
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={draft.active !== false} onChange={(e) => set({ active: e.target.checked })} />
              Active
            </label>
          )}
          {error && <ErrorState message={error} />}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={save.isPending} onClick={submit}>{save.isPending ? 'Saving…' : 'Save range'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Packages ─────────────────────────────────────────────────────

function PackagesPanel({ canEdit }: { canEdit: boolean }) {
  const packages = useLabPackages();
  const tests = useLabTests({});
  const save = useSavePackage();
  const [editing, setEditing] = useState<{ id?: number; code: string; name: string; price: string; testIds: number[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!editing) return;
    setError(null);
    try {
      await save.mutateAsync({
        id: editing.id,
        body: {
          code: editing.code, name: editing.name, price: editing.price,
          tests: editing.testIds.map((test_id) => ({ test_id })),
        },
      });
      setEditing(null);
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <div className="space-y-2">
      <p className="text-[11px] text-muted-foreground">
        A package is a priced bundle. Ordering it puts one row per member test on the order — collection,
        result entry and verification never see a package — and bills the package’s own price once.
      </p>
      {canEdit && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setEditing({ code: '', name: '', price: '0', testIds: [] })}>+ New package</Button>
        </div>
      )}
      {packages.isLoading && <LoadingState label="Loading…" />}
      {!packages.isLoading && (packages.data ?? []).length === 0 && <EmptyState label="No packages configured." />}
      {(packages.data ?? []).length > 0 && (
        <div className="rounded-xl border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead><TableHead>Package</TableHead>
                <TableHead>Tests</TableHead><TableHead className="text-right">Price</TableHead><TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(packages.data ?? []).map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-mono text-[12px]">{p.code}</TableCell>
                  <TableCell>{p.name}{!p.active && <Badge variant="neutral" className="ml-1">Inactive</Badge>}</TableCell>
                  <TableCell className="text-[12px] text-muted-foreground">
                    {p.tests.map((t) => t.test_code).join(', ') || '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">Rs. {Number(p.price).toFixed(2)}</TableCell>
                  <TableCell className="text-right">
                    {canEdit && (
                      <Button variant="link" size="sm"
                        onClick={() => setEditing({
                          id: p.id, code: p.code, name: p.name, price: p.price,
                          testIds: p.tests.map((t) => t.test_id),
                        })}>
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => { if (!o) { setEditing(null); setError(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit package' : 'New package'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField label="Code" required>
                  <Input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} />
                </FormField>
                <FormField label="Price (Rs.)" required>
                  <Input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
                </FormField>
              </div>
              <FormField label="Name" required>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </FormField>
              <FormField label="Tests" required>
                <div className="max-h-48 overflow-auto rounded-lg border border-border">
                  {(tests.data ?? []).map((t) => (
                    <label key={t.id} className="flex cursor-pointer items-center gap-2 border-b border-border px-2 py-1 text-[13px] last:border-0 hover:bg-muted/50">
                      <input
                        type="checkbox"
                        checked={editing.testIds.includes(t.id)}
                        onChange={(e) => setEditing({
                          ...editing,
                          testIds: e.target.checked
                            ? [...editing.testIds, t.id]
                            : editing.testIds.filter((id) => id !== t.id),
                        })}
                      />
                      {t.name} <span className="font-mono text-[11px] text-muted-foreground">{t.code}</span>
                    </label>
                  ))}
                </div>
              </FormField>
              {error && <ErrorState message={error} />}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEditing(null); setError(null); }}>Cancel</Button>
            <Button disabled={save.isPending || !editing?.testIds.length} onClick={submit}>
              {save.isPending ? 'Saving…' : 'Save package'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
